import { useState, useEffect, useCallback, useMemo } from "react";
import { authrulesApi } from "@/api/authrules.api";
import {
    Alert, Badge, Field, Input, Select, Btn, Modal, Th, Td, Empty, Tabs, limpia,
} from "./authrulesUi";

// ============================================================================
//  Bloqueos · Clearcom
//   - Generar:   constructor masivo (empresa -> cuentas -> IPs -> modo)
//   - Reglas:    buscador general + paginacion del servidor
//   - Historial: bitacora append-only
// ============================================================================

const TABS = [
    { id: "generar", label: "Generar" },
    { id: "reglas", label: "Reglas" },
    { id: "historial", label: "Historial" },
];

const REGLA_VACIA = {
    i_account: "", i_protocol: "1", remote_ip: "", incoming_cli: "",
    incoming_cld: "", to_domain: "", from_domain: "",
    cli_translation_rule: "", cld_translation_rule: "",
    i_tariff: "", i_routing_group: "", max_sessions: "", max_cps: "",
};

// ════════════════════════════════════════════════════════════════════════════
//  GENERAR — constructor masivo
// ════════════════════════════════════════════════════════════════════════════

function GenerarTab() {
    const [empresas, setEmpresas] = useState([]);
    const [empresaId, setEmpresaId] = useState("");
    const [cuentas, setCuentas] = useState([]);
    const [catalogoIps, setCatalogoIps] = useState([]);
    const [troncales, setTroncales] = useState([]);
    const [netbox, setNetbox] = useState([]);
    const [savingNetbox, setSavingNetbox] = useState(null);
    const [destino, setDestino] = useState("ambos");

    const [selAccounts, setSelAccounts] = useState(() => new Set());
    const [selTroncales, setSelTroncales] = useState(() => new Set());
    const [ipsInput, setIpsInput] = useState("");
    const [cliInput, setCliInput] = useState("");
    const [cldInput, setCldInput] = useState("");
    const [adv, setAdv] = useState({
        i_protocol: "1", to_domain: "", from_domain: "",
        cli_tr: "", cld_tr: "",
        i_tariff: "", i_routing_group: "", max_sessions: "", max_cps: "",
    });

    const parse = (s) =>
        Array.from(new Set(String(s).split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean)));
    const ips = useMemo(() => parse(ipsInput), [ipsInput]);
    const clis = useMemo(() => parse(cliInput), [cliInput]);
    const clds = useMemo(() => parse(cldInput), [cldInput]);

    const [preview, setPreview] = useState(null);
    const [selPlan, setSelPlan] = useState(() => new Set());
    const [editPlan, setEditPlan] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // cargar empresas al montar
    useEffect(() => {
        (async () => {
            setError("");
            try {
                const res = await authrulesApi.opciones({ todas: 1 });
                setEmpresas(res.empresas || []);
            } catch (e) {
                setError(e.message);
            }
        })();
    }, []);

    const recargarNetbox = async (id) => {
        const emp = id ?? empresaId;
        if (!emp) return;
        try {
            const res = await authrulesApi.opciones({ empresa_id: emp });
            setNetbox(res.netbox || []);
        } catch {
            /* noop: el panel es informativo */
        }
    };

    // Bloqueo/desbloqueo admin de un customer de Netbox (saldo 0 = bloquear, 1 = desbloquear).
    const bloquearNetbox = async (iCustomer, saldo) => {
        setSavingNetbox(iCustomer);
        setError("");
        try {
            await authrulesApi.blaster({ accion: "netbox_bloqueo", i_customer: iCustomer, saldo });
            await recargarNetbox();
        } catch (e) {
            setError(e.message);
        } finally {
            setSavingNetbox(null);
        }
    };

    const onEmpresa = async (e) => {
        const id = e.target.value;
        setEmpresaId(id);
        setPreview(null);
        setResultado(null);
        setCuentas([]);
        setCatalogoIps([]);
        setTroncales([]);
        setNetbox([]);
        setIpsInput("");
        setSelAccounts(new Set());
        setSelTroncales(new Set());
        if (!id) return;
        setLoading(true);
        setError("");
        try {
            const res = await authrulesApi.opciones({ empresa_id: id });
            const cs = res.cuentas || [];
            const ts = res.troncales || [];
            const ipsList = res.ips || [];
            setCuentas(cs);
            setCatalogoIps(ipsList);
            setTroncales(ts);
            setNetbox(res.netbox || []);
            setSelAccounts(new Set(cs.map((c) => c.i_account)));
            setSelTroncales(new Set(ts.map((t) => t.id)));
            // Precargar las IPs de la(s) cuenta(s) en la caja para que el preview
            // cruce troncal/CLI × IP y aparezca remote_ip. Editable / borrable.
            const ipsTexto = Array.from(
                new Set(ipsList.map((r) => r.remote_ip).filter(Boolean)),
            );
            setIpsInput(ipsTexto.join("\n"));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleAccount = (id) => {
        const next = new Set(selAccounts);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelAccounts(next);
    };

    // Troncales de las cuentas elegidas (la relacion empresa-cliente-troncal).
    const troncalesVisibles = useMemo(
        () => troncales.filter((t) => selAccounts.has(t.i_account)),
        [troncales, selAccounts],
    );
    const toggleTroncal = (id) => {
        const next = new Set(selTroncales);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelTroncales(next);
    };
    const todasTroncales =
        troncalesVisibles.length > 0 && troncalesVisibles.every((t) => selTroncales.has(t.id));
    const toggleTodasTroncales = () => {
        const next = new Set(selTroncales);
        if (todasTroncales) troncalesVisibles.forEach((t) => next.delete(t.id));
        else troncalesVisibles.forEach((t) => next.add(t.id));
        setSelTroncales(next);
    };
    // parejas (cuenta, troncal) realmente aplicables = troncales visibles seleccionadas
    const troncalPairs = useMemo(
        () =>
            troncalesVisibles
                .filter((t) => selTroncales.has(t.id))
                .map((t) => ({ i_account: t.i_account, troncal: t.troncal })),
        [troncalesVisibles, selTroncales],
    );

    // IPs del catalogo de las cuentas elegidas (para el boton "usar catalogo").
    const ipsCatalogo = useMemo(() => {
        const s = new Set();
        for (const r of catalogoIps) if (selAccounts.has(r.i_account)) s.add(r.remote_ip);
        return [...s];
    }, [catalogoIps, selAccounts]);

    const usarCatalogo = () => {
        const merged = parse(ipsInput + "\n" + ipsCatalogo.join("\n"));
        setIpsInput(merged.join("\n"));
    };

    const hayNumeros = clis.length > 0 || clds.length > 0 || troncalPairs.length > 0;
    const puedeGenerar =
        !!empresaId && selAccounts.size > 0 && (ips.length > 0 || hayNumeros);

    const totalEstimado = useMemo(() => {
        const porIp = hayNumeros ? clis.length + clds.length + troncalPairs.length : 1;
        const nIps = ips.length || (hayNumeros ? 1 : 0);
        return selAccounts.size * nIps * porIp;
    }, [selAccounts, ips, clis, clds, troncalPairs, hayNumeros]);

    const payload = (dry) =>
        limpia({
            empresa_id: empresaId,
            i_accounts: [...selAccounts],
            troncales: troncalPairs.length ? troncalPairs : undefined,
            ips: ips.length ? ips : undefined,
            clis: clis.length ? clis : undefined,
            clds: clds.length ? clds : undefined,
            i_protocol: adv.i_protocol || undefined,
            to_domain: adv.to_domain || undefined,
            from_domain: adv.from_domain || undefined,
            cli_tr: adv.cli_tr || undefined,
            cld_tr: adv.cld_tr || undefined,
            i_tariff: adv.i_tariff || undefined,
            i_routing_group: adv.i_routing_group || undefined,
            max_sessions: adv.max_sessions || undefined,
            max_cps: adv.max_cps || undefined,
            destino: destino !== "ambos" ? destino : undefined,
            dry_run: dry ? 1 : undefined,
        });

    const previsualizar = async () => {
        setLoading(true);
        setError("");
        setResultado(null);
        setPreview(null);
        try {
            const res = await authrulesApi.generar(payload(true));
            setPreview(res);
            // Solo se preseleccionan (y aplican) las nuevas; las "ya existe" van en gris.
            setSelPlan(
                new Set(
                    (res.plan || [])
                        .map((p, i) => (p.ya_existe ? -1 : i))
                        .filter((i) => i >= 0),
                ),
            );
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const togglePlan = (i) =>
        setSelPlan((prev) => {
            if (preview?.plan?.[i]?.ya_existe) return prev; // no se pueden marcar
            const n = new Set(prev);
            n.has(i) ? n.delete(i) : n.add(i);
            return n;
        });

    const toggleTodoPlan = () => {
        if (!preview?.plan) return;
        const nuevas = preview.plan
            .map((p, i) => (p.ya_existe ? -1 : i))
            .filter((i) => i >= 0);
        if (selPlan.size === nuevas.length) setSelPlan(new Set());
        else setSelPlan(new Set(nuevas));
    };

    const abrirEditPlan = (i, p) =>
        setEditPlan({
            index: i,
            titulo: `Editar regla del plan (cuenta ${p.i_account})`,
            ocultarCuentaProto: true,
            data: { ...p, i_protocol: String(p.i_protocol ?? 1) },
        });

    const guardarEditPlan = (form) => {
        setPreview((prev) => {
            if (!prev) return prev;
            const plan = prev.plan.slice();
            const orig = plan[editPlan.index] || {};
            plan[editPlan.index] = limpia({
                i_account: orig.i_account,
                cliente_id: orig.cliente_id,
                empresa_id: orig.empresa_id,
                i_protocol: form.i_protocol,
                remote_ip: form.remote_ip,
                incoming_cli: form.incoming_cli,
                incoming_cld: form.incoming_cld,
                to_domain: form.to_domain,
                from_domain: form.from_domain,
                cli_translation_rule: form.cli_translation_rule,
                cld_translation_rule: form.cld_translation_rule,
                i_tariff: form.i_tariff,
                i_routing_group: form.i_routing_group,
                max_sessions: form.max_sessions,
                max_cps: form.max_cps,
            });
            return { ...prev, plan };
        });
        setEditPlan(null);
    };

    const aplicar = async () => {
        if (!preview?.plan) return;
        // Netbox-only: no importan las reglas seleccionadas (solo las cuentas),
        // se manda todo el plan para resolver el i_customer de la empresa.
        const rules =
            destino === "netbox"
                ? preview.plan
                : preview.plan.filter((_, i) => selPlan.has(i));
        if (!rules.length) return;
        const msg =
            destino === "netbox"
                ? "Bloquear Netbox (credit_limit) de esta empresa?"
                : destino === "clearcom"
                    ? `Enviar ${rules.length} reglas a Sippy (solo Clearcom)?`
                    : `Enviar ${rules.length} reglas a Sippy y bloquear Netbox?`;
        if (!window.confirm(msg)) return;
        setLoading(true);
        setError("");
        try {
            setResultado(await authrulesApi.generar({ reglas: rules, destino }));
            setPreview(null);
            await recargarNetbox();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Alert kind="error" onClose={() => setError("")}>{error}</Alert>

            <div className="grid gap-4 lg:grid-cols-3">
                {/* 1. empresa + cuentas */}
                <div className="rounded-lg border border-slate-200 p-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">1 · Empresa y cuentas</div>
                    <Field label="Empresa">
                        <Select value={empresaId} onChange={onEmpresa}>
                            <option value="">Selecciona…</option>
                            {empresas.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                        </Select>
                    </Field>

                    {cuentas.length > 0 && (
                        <div className="mt-3 space-y-1">
                            <div className="text-xs text-slate-500">Cuentas (i_account):</div>
                            {cuentas.map((c) => (
                                <label key={c.i_account} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-slate-50">
                                    <input type="checkbox" checked={selAccounts.has(c.i_account)} onChange={() => toggleAccount(c.i_account)} className="accent-teal-600" />
                                    <span className="font-mono text-xs text-slate-700">{c.i_account}</span>
                                    <span className="text-sm text-slate-600">{c.cuenta_nombre || c.cliente_nombre || "—"}</span>
                                </label>
                            ))}
                        </div>
                    )}
                    {empresaId && cuentas.length === 0 && !loading && (
                        <p className="mt-3 text-sm text-slate-400">Esta empresa no tiene cuentas Clearcom mapeadas.</p>
                    )}

                    {troncalesVisibles.length > 0 && (
                        <div className="mt-4 border-t border-slate-100 pt-3">
                            <div className="mb-1 flex items-center justify-between">
                                <span className="text-xs text-slate-500">Troncales del cliente (→ CLI):</span>
                                <button onClick={toggleTodasTroncales} className="text-xs font-medium text-teal-600 hover:underline">
                                    {todasTroncales ? "Quitar todas" : "Todas"}
                                </button>
                            </div>
                            <div className="max-h-48 space-y-1 overflow-y-auto">
                                {troncalesVisibles.map((t) => (
                                    <label key={t.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-slate-50">
                                        <input type="checkbox" checked={selTroncales.has(t.id)} onChange={() => toggleTroncal(t.id)} className="accent-teal-600" />
                                        <span className="font-mono text-xs text-slate-700">{t.troncal}</span>
                                        <span className="ml-auto truncate text-xs text-slate-400">{t.cliente_nombre || `cta ${t.i_account}`}</span>
                                    </label>
                                ))}
                            </div>
                            <p className="mt-1 text-[11px] text-slate-400">Cada troncal se da de alta en la cuenta de su cliente.</p>
                        </div>
                    )}
                    {cuentas.length > 0 && troncales.length > 0 && troncalesVisibles.length === 0 && !loading && (
                        <p className="mt-3 text-xs text-slate-400">Sin troncales para las cuentas marcadas.</p>
                    )}
                    {cuentas.length > 0 && troncales.length === 0 && !loading && (
                        <p className="mt-3 text-xs text-slate-400">Este cliente no tiene troncales; usa CLI/CLD o IPs.</p>
                    )}

                    {netbox.length > 0 && (
                        <div className="mt-4 border-t border-slate-100 pt-3">
                            <span className="text-xs text-slate-500">Netbox (credit_limit):</span>
                            <div className="mt-1 space-y-1">
                                {netbox.map((n) => {
                                    const bloqueado = Number(n.bloqueado) === 1;
                                    const saving = savingNetbox === n.i_customer;
                                    return (
                                        <div key={n.i_customer} className="flex items-center gap-2 text-xs">
                                            <span className="truncate text-slate-700">{n.nombre}</span>
                                            <span className="text-slate-400">({n.i_customer})</span>
                                            <span className="ml-auto font-mono text-slate-700">
                                                ${Number(n.credit_limit ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                            </span>
                                            {bloqueado
                                                ? <Badge tone="amber">bloqueado</Badge>
                                                : <Badge tone="teal">activo</Badge>}
                                            <button
                                                type="button"
                                                onClick={() => bloquearNetbox(n.i_customer, bloqueado ? 1 : 0)}
                                                disabled={saving}
                                                className={`rounded px-2 py-0.5 text-[11px] font-medium ${bloqueado
                                                    ? "bg-teal-50 text-teal-700 hover:bg-teal-100"
                                                    : "bg-amber-50 text-amber-700 hover:bg-amber-100"
                                                    } disabled:opacity-50`}
                                            >
                                                {saving ? "…" : bloqueado ? "Desbloquear" : "Bloquear"}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="mt-1 text-[11px] text-slate-400">Bloquea/desbloquea Netbox directo aqui, o acoplado a las reglas (aplicar bloquea, borrar desbloquea). El credit_limit se refleja segun el sync.</p>
                        </div>
                    )}
                </div>

                {/* 2. IPs */}
                <div className="rounded-lg border border-slate-200 p-4">
                    <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">2 · IPs (remote_ip)</span>
                        {ipsCatalogo.length > 0 && (
                            <button onClick={usarCatalogo} className="text-xs font-medium text-teal-600 hover:underline">
                                usar catalogo ({ipsCatalogo.length})
                            </button>
                        )}
                    </div>
                    <textarea
                        value={ipsInput}
                        onChange={(e) => setIpsInput(e.target.value)}
                        rows={8}
                        placeholder={"Pega las IPs separadas por coma o salto de linea\n192.0.2.10, 192.0.2.11\n50.30.32.69"}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                    />
                    <p className="mt-2 text-xs text-slate-400">
                        {ips.length} IP{ips.length === 1 ? "" : "s"}. Si lo dejas vacio, las reglas se crean sin remote_ip (match solo por numero).
                    </p>
                </div>

                {/* 3. datos de la regla */}
                <div className="rounded-lg border border-slate-200 p-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">3 · Datos de la regla</div>

                    <div className="mb-1 text-xs font-medium text-slate-500">Incoming CLI/ANI</div>
                    <textarea
                        value={cliInput}
                        onChange={(e) => setCliInput(e.target.value)}
                        rows={4}
                        placeholder={"Un numero por linea o por coma\n521908201001\n521941481026"}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                    />

                    <div className="mb-1 mt-3 text-xs font-medium text-slate-500">Incoming CLD/DNIS <span className="text-slate-400">(opcional)</span></div>
                    <textarea
                        value={cldInput}
                        onChange={(e) => setCldInput(e.target.value)}
                        rows={2}
                        placeholder={"Normalmente vacio"}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                    />

                    <p className="mt-2 text-[11px] text-slate-400">
                        Cada numero genera una regla por IP. La translation rule identidad (s/^N$/N/) se agrega sola;
                        puedes cambiarla en Avanzado. Sin numeros = una regla por IP (solo remote_ip).
                    </p>

                    <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                        {selAccounts.size} cuentas · {ips.length || "—"} IPs · {clis.length} CLI · {troncalPairs.length} troncales · {clds.length} CLD
                        <span className="ml-1 text-slate-400">≈ {totalEstimado} reglas</span>
                    </div>

                    <details className="mt-3 rounded-lg border border-slate-200 p-3">
                        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-400">Avanzado (opcional)</summary>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                            <Field label="Protocol">
                                <Select value={adv.i_protocol} onChange={(e) => setAdv({ ...adv, i_protocol: e.target.value })}>
                                    <option value="1">SIP</option>
                                    <option value="2">H.323</option>
                                    <option value="4">IAX2</option>
                                </Select>
                            </Field>
                            <Field label="Tariff (i_tariff)"><Input value={adv.i_tariff} onChange={(e) => setAdv({ ...adv, i_tariff: e.target.value })} placeholder="hereda" /></Field>
                            <Field label="To Domain"><Input value={adv.to_domain} onChange={(e) => setAdv({ ...adv, to_domain: e.target.value })} placeholder="—" /></Field>
                            <Field label="From Domain"><Input value={adv.from_domain} onChange={(e) => setAdv({ ...adv, from_domain: e.target.value })} placeholder="—" /></Field>
                            <Field label="Routing Group"><Input value={adv.i_routing_group} onChange={(e) => setAdv({ ...adv, i_routing_group: e.target.value })} placeholder="hereda" /></Field>
                            <Field label="Max Sessions"><Input value={adv.max_sessions} onChange={(e) => setAdv({ ...adv, max_sessions: e.target.value })} placeholder="∞" /></Field>
                            <Field label="Max CPS"><Input value={adv.max_cps} onChange={(e) => setAdv({ ...adv, max_cps: e.target.value })} placeholder="∞" /></Field>
                            <div className="col-span-2">
                                <Field label="CLI Tr. Rule"><Input value={adv.cli_tr} onChange={(e) => setAdv({ ...adv, cli_tr: e.target.value })} placeholder="vacio = s/^{n}$/{n}/  ·  '-' = ninguna" /></Field>
                            </div>
                            <div className="col-span-2">
                                <Field label="CLD Tr. Rule"><Input value={adv.cld_tr} onChange={(e) => setAdv({ ...adv, cld_tr: e.target.value })} placeholder="vacio = identidad  ·  usa {n} = numero" /></Field>
                            </div>
                        </div>
                        <p className="mt-2 text-xs text-slate-400">Vacio = hereda de la cuenta. Se aplica a todas las reglas.</p>
                    </details>

                    <div className="mt-3">
                        <Field label="Destino del bloqueo">
                            <Select value={destino} onChange={(e) => setDestino(e.target.value)}>
                                <option value="ambos">Ambos (Clearcom + Netbox)</option>
                                <option value="clearcom">Solo Clearcom (reglas)</option>
                                <option value="netbox">Solo Netbox (credit_limit)</option>
                            </Select>
                        </Field>
                    </div>

                    <div className="mt-3 flex flex-col gap-2">
                        <Btn variant="ghost" onClick={previsualizar} disabled={loading || !puedeGenerar}>
                            {loading ? "…" : "Previsualizar"}
                        </Btn>
                        <Btn
                            variant="primary"
                            onClick={aplicar}
                            disabled={loading || !preview || (destino !== "netbox" && selPlan.size === 0)}
                        >
                            {destino === "netbox"
                                ? "Aplicar (solo Netbox)"
                                : `Aplicar ${preview && selPlan.size > 0 ? `(${selPlan.size})` : ""}`}
                        </Btn>
                    </div>
                </div>
            </div>

            {/* preview */}
            {preview && (
                <div className="mt-5">
                    <div className="mb-3 flex items-center gap-3">
                        <Badge tone="amber">dry-run</Badge>
                        <span className="text-sm text-slate-600">
                            empresa <b>{preview.empresa_id}</b> ·{" "}
                            {destino === "netbox"
                                ? "solo se bloqueará Netbox (credit_limit); Clearcom no se toca"
                                : destino === "clearcom"
                                    ? `${preview.total} reglas a crear (solo Clearcom; Netbox no se toca)`
                                    : `${preview.total} reglas a crear + bloqueo Netbox`}
                        </span>
                    </div>

                    {destino === "netbox" ? (
                        <div className="mb-4 rounded-lg border border-slate-200 p-4">
                            <div className="text-xs text-slate-500 mb-2">Se bloqueará en Netbox (credit_limit → 0):</div>
                            {netbox.length > 0 ? (
                                <div className="space-y-1">
                                    {netbox.map((n) => (
                                        <div key={n.i_customer} className="flex items-center gap-2 text-sm">
                                            <span className="text-slate-700">{n.nombre}</span>
                                            <span className="text-slate-400">({n.i_customer})</span>
                                            <span className="ml-auto font-mono text-slate-700">
                                                ${Number(n.credit_limit ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                            </span>
                                            {Number(n.bloqueado) === 1
                                                ? <Badge tone="amber">ya bloqueado</Badge>
                                                : <Badge tone="teal">activo</Badge>}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400">Esta empresa no tiene un customer de Netbox mapeado.</p>
                            )}
                            <p className="mt-2 text-[11px] text-slate-400">No se creará ninguna regla en Clearcom.</p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                                {(preview.por_cuenta || []).map((c) => (
                                    <div key={c.i_account} className="rounded-lg border border-slate-200 p-3">
                                        <div className="font-mono text-xs text-slate-400">cuenta {c.i_account}</div>
                                        <div className="mt-1 text-2xl font-semibold text-teal-700">{c.armadas}</div>
                                        <div className="text-xs text-slate-500">
                                            {c.ips} IPs · {c.clis} CLI{c.troncales ? ` (${c.troncales} tronc)` : ""} · {c.clds} CLD · {c.omitidas} ya existen
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {preview.total > 0 ? (
                                <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200">
                                    <table className="w-full border-collapse">
                                        <thead className="sticky top-0 bg-white">
                                            <tr>
                                                <Th>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            selPlan.size > 0 &&
                                                            selPlan.size === preview.plan.filter((p) => !p.ya_existe).length
                                                        }
                                                        onChange={toggleTodoPlan}
                                                        className="accent-teal-600"
                                                    />
                                                </Th>
                                                <Th>cuenta</Th><Th>remote_ip</Th><Th>incoming_cli</Th><Th>incoming_cld</Th><Th>tr_rule</Th><Th>avanzado</Th><Th></Th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.plan.map((p, i) => {
                                                const avz = [
                                                    p.i_tariff != null && p.i_tariff !== "" ? `tar:${p.i_tariff}` : null,
                                                    p.i_routing_group != null && p.i_routing_group !== "" ? `rg:${p.i_routing_group}` : null,
                                                    p.max_sessions != null && p.max_sessions !== "" ? `ses:${p.max_sessions}` : null,
                                                    p.max_cps != null && p.max_cps !== "" ? `cps:${p.max_cps}` : null,
                                                    p.to_domain ? `to:${p.to_domain}` : null,
                                                    p.from_domain ? `from:${p.from_domain}` : null,
                                                ].filter(Boolean).join(" · ");
                                                const ex = p.ya_existe;
                                                return (
                                                    <tr key={i} className={ex ? "bg-slate-50 text-slate-400" : (selPlan.has(i) ? "hover:bg-slate-50" : "bg-slate-50 opacity-50")}>
                                                        <Td>
                                                            <input type="checkbox" checked={selPlan.has(i)} disabled={ex} onChange={() => togglePlan(i)} className="accent-teal-600" />
                                                        </Td>
                                                        <Td mono>{p.i_account}</Td>
                                                        <Td mono>{p.remote_ip || "—"}</Td>
                                                        <Td mono>{p.incoming_cli || "—"}</Td>
                                                        <Td mono>{p.incoming_cld || "—"}</Td>
                                                        <Td mono>{p.cli_translation_rule || p.cld_translation_rule || "—"}</Td>
                                                        <Td mono>{avz || "—"}</Td>
                                                        <Td>
                                                            {ex
                                                                ? <Badge>ya existe</Badge>
                                                                : <button onClick={() => abrirEditPlan(i, p)} className="text-teal-600 hover:underline">Editar</button>}
                                                        </Td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <Empty>Nada que crear: ya existen todas, o no hay IPs/numeros para lo elegido.</Empty>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* resultado */}
            {resultado && (
                <div className="mt-5 rounded-lg border border-teal-200 bg-teal-50 p-4">
                    <div className="flex items-center gap-3">
                        <Badge tone="teal">aplicado</Badge>
                        <span className="text-sm text-slate-700">
                            empresa {resultado.empresa_id}: <b>{resultado.creadas}</b> creadas
                            {resultado.fallidas ? <> · <span className="text-rose-600">{resultado.fallidas} fallidas</span></> : null}
                        </span>
                    </div>
                    {resultado.errores && resultado.errores.length > 0 && (
                        <ul className="mt-2 list-disc pl-5 text-xs text-rose-600">
                            {resultado.errores.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    )}
                </div>
            )}

            <EditarReglaModal modal={editPlan} onClose={() => setEditPlan(null)} onSave={guardarEditPlan} />
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
//  REGLAS — buscador general + paginacion
// ════════════════════════════════════════════════════════════════════════════

function ReglasTab({ onNueva }) {
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [orden, setOrden] = useState("asc");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [ok, setOk] = useState("");
    const [modal, setModal] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await authrulesApi.reglas({ accion: "listar", q: q || undefined, page, limit, orden });
            setRows(res.data || []);
            setTotal(res.total || 0);
            setTotalPages(res.total_pages || 1);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [q, page, limit, orden]);

    // debounce de busqueda + paginacion
    useEffect(() => {
        const t = setTimeout(cargar, 300);
        return () => clearTimeout(t);
    }, [cargar]);

    // al cambiar la busqueda, vuelve a la pagina 1
    useEffect(() => { setPage(1); }, [q]);

    const guardar = async (form) => {
        setError("");
        setOk("");
        try {
            const cambios = limpia(form);
            await authrulesApi.reglas({ accion: "editar", i_authentication: modal.data.i_authentication, ...cambios });
            setOk("Regla actualizada.");
            // Parchea la fila al instante (la tabla local se sincroniza con retraso).
            setRows((prev) =>
                prev.map((x) =>
                    x.i_authentication === modal.data.i_authentication ? { ...x, ...cambios } : x,
                ),
            );
            setModal(null);
        } catch (e) {
            setError(e.message);
        }
    };

    const borrar = async (r) => {
        if (!window.confirm(`Borrar regla i_authentication=${r.i_authentication}?`)) return;
        setError("");
        setOk("");
        try {
            await authrulesApi.reglas({ accion: "borrar", i_authentication: r.i_authentication });
            setOk(`Regla ${r.i_authentication} borrada.`);
            // La tabla local se sincroniza con retraso: quita la fila al instante
            // para no dejar un fantasma que se pueda volver a borrar.
            setRows((prev) => prev.filter((x) => x.i_authentication !== r.i_authentication));
            setTotal((t) => Math.max(0, t - 1));
        } catch (e) {
            setError(e.message);
        }
    };

    return (
        <div>
            <Alert kind="error" onClose={() => setError("")}>{error}</Alert>
            <Alert kind="ok" onClose={() => setOk("")}>{ok}</Alert>

            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="min-w-64 flex-1">
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar por IP, CLI, cuenta, empresa o cliente…"
                    />
                </div>
                <span className="text-sm text-slate-400">{total} reglas</span>
                <Btn variant="ghost" onClick={() => { setPage(1); setOrden((o) => (o === "asc" ? "desc" : "asc")); }}>
                    i_auth {orden === "asc" ? "↑ asc" : "↓ desc"}
                </Btn>
                <Btn variant="ghost" onClick={onNueva}>+ Generar reglas</Btn>
            </div>

            {rows.length === 0 && !loading ? (
                <Empty>{q ? "Sin coincidencias para la busqueda." : "Sin reglas."}</Empty>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr><Th>i_auth</Th><Th>cuenta</Th><Th>empresa / cliente</Th><Th>remote_ip</Th><Th>incoming_cli</Th><Th>cli_tr_rule</Th><Th></Th></tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.i_authentication} className="hover:bg-slate-50">
                                        <Td mono>{r.i_authentication}</Td>
                                        <Td>{r.cuenta_nombre || r.i_account}</Td>
                                        <Td>{[r.empresa_nombre, r.cliente_nombre].filter(Boolean).join(" / ") || "—"}</Td>
                                        <Td mono>{r.remote_ip || "—"}</Td>
                                        <Td mono>{r.incoming_cli || "—"}</Td>
                                        <Td mono>{r.cli_translation_rule || "—"}</Td>
                                        <Td>
                                            <div className="flex gap-2">
                                                <button onClick={() => setModal({ data: { ...REGLA_VACIA, ...r, i_protocol: String(r.i_protocol ?? 1) } })} className="text-teal-600 hover:underline">Editar</button>
                                                <button onClick={() => borrar(r)} className="text-rose-600 hover:underline">Borrar</button>
                                            </div>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-sm text-slate-400">Pagina {page} de {totalPages}</span>
                        <div className="flex gap-2">
                            <Btn variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>Anterior</Btn>
                            <Btn variant="ghost" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>Siguiente</Btn>
                        </div>
                    </div>
                </>
            )}

            <EditarReglaModal modal={modal} onClose={() => setModal(null)} onSave={guardar} />
        </div>
    );
}

function EditarReglaModal({ modal, onClose, onSave }) {
    const [form, setForm] = useState(REGLA_VACIA);
    useEffect(() => { if (modal) setForm({ ...REGLA_VACIA, ...modal.data }); }, [modal]);
    if (!modal) return null;
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const ocultar = modal.ocultarCuentaProto;

    return (
        <Modal
            open={!!modal}
            onClose={onClose}
            title={modal.titulo || `Editar regla ${modal.data.i_authentication ?? ""}`}
            footer={<><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn onClick={() => onSave(form)}>Guardar</Btn></>}
        >
            <div className="grid grid-cols-2 gap-3">
                {!ocultar && <Field label="i_account"><Input value={form.i_account} disabled /></Field>}
                {!ocultar && <Field label="i_protocol"><Input value={form.i_protocol} onChange={set("i_protocol")} /></Field>}
                <Field label="remote_ip"><Input value={form.remote_ip} onChange={set("remote_ip")} /></Field>
                <Field label="incoming_cli"><Input value={form.incoming_cli} onChange={set("incoming_cli")} /></Field>
                <Field label="incoming_cld"><Input value={form.incoming_cld} onChange={set("incoming_cld")} /></Field>
                <Field label="to_domain"><Input value={form.to_domain} onChange={set("to_domain")} /></Field>
                <Field label="from_domain"><Input value={form.from_domain} onChange={set("from_domain")} /></Field>
                <div className="col-span-2"><Field label="cli_translation_rule"><Input value={form.cli_translation_rule} onChange={set("cli_translation_rule")} /></Field></div>
                <div className="col-span-2"><Field label="cld_translation_rule"><Input value={form.cld_translation_rule} onChange={set("cld_translation_rule")} /></Field></div>
                <div className="col-span-2 mt-1 border-t border-slate-100 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Avanzado (opcional · vacio = hereda de la cuenta)</div>
                <Field label="i_tariff"><Input value={form.i_tariff} onChange={set("i_tariff")} placeholder="null = base" /></Field>
                <Field label="i_routing_group"><Input value={form.i_routing_group} onChange={set("i_routing_group")} placeholder="null = cuenta" /></Field>
                <Field label="max_sessions"><Input value={form.max_sessions} onChange={set("max_sessions")} placeholder="-1 = ilimitado" /></Field>
                <Field label="max_cps"><Input value={form.max_cps} onChange={set("max_cps")} placeholder="null = ilimitado" /></Field>
            </div>
        </Modal>
    );
}

// ════════════════════════════════════════════════════════════════════════════
//  HISTORIAL
// ════════════════════════════════════════════════════════════════════════════

function HistorialTab() {
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [orden, setOrden] = useState("desc");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [sincronizando, setSincronizando] = useState(false);
    const [error, setError] = useState("");
    const [ok, setOk] = useState("");
    const [sel, setSel] = useState(() => new Set());

    const cargar = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await authrulesApi.historial({ q: q || undefined, page, limit, orden });
            setRows(res.data || []);
            setTotal(res.total || 0);
            setTotalPages(res.total_pages || 1);
            setSel(new Set());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [q, page, limit, orden]);

    useEffect(() => {
        const t = setTimeout(cargar, 300);
        return () => clearTimeout(t);
    }, [cargar]);

    useEffect(() => { setPage(1); }, [q]);

    const fecha = (v) => (v ? String(v).replace("T", " ").slice(0, 19) : "—");

    const toggle = (id) =>
        setSel((prev) => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    const toggleTodos = () => {
        if (sel.size === rows.length) setSel(new Set());
        else setSel(new Set(rows.map((r) => r.id)));
    };

    const reAlta = async () => {
        const elegidas = rows.filter((r) => sel.has(r.id));
        if (!elegidas.length) return;
        const reglas = elegidas.map((r) =>
            limpia({
                i_account: r.i_account,
                i_protocol: r.i_protocol,
                remote_ip: r.remote_ip,
                incoming_cli: r.incoming_cli,
                incoming_cld: r.incoming_cld,
                to_domain: r.to_domain,
                from_domain: r.from_domain,
                cli_translation_rule: r.cli_translation_rule,
                cld_translation_rule: r.cld_translation_rule,
                i_tariff: r.i_tariff,
                i_routing_group: r.i_routing_group,
                max_sessions: r.max_sessions,
                max_cps: r.max_cps,
                empresa_id: r.empresa_id,
                cliente_id: r.cliente_id,
            }),
        );
        if (!window.confirm(`Volver a dar de alta ${reglas.length} reglas en Sippy?`)) return;
        setLoading(true);
        setError("");
        setOk("");
        try {
            const res = await authrulesApi.generar({ reglas });
            setOk(`${res.creadas} creadas${res.fallidas ? ` · ${res.fallidas} fallidas` : ""}.`);
            if (res.errores && res.errores.length) setError(res.errores.slice(0, 10).join(" · "));
            cargar();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const sincronizar = async () => {
        setSincronizando(true);
        setError("");
        setOk("");
        try {
            const res = await authrulesApi.historial({ accion: "sincronizar" });
            const s = res?.sync;
            if (s && !s.ok) {
                setError(`El sync de Sippy terminó con código ${s.codigo}. Revisa el log del servidor.`);
            } else {
                setOk("Reglas sincronizadas desde Sippy.");
            }
            await cargar();
        } catch (e) {
            setError(e.message);
        } finally {
            setSincronizando(false);
        }
    };

    return (
        <div className="relative">
            {sincronizando && (
                <div className="absolute inset-0 z-20 flex items-start justify-center rounded-lg bg-white/70 backdrop-blur-sm">
                    <div className="sticky top-24 mt-24 flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white px-8 py-6 shadow-xl">
                        <svg className="h-9 w-9 animate-spin text-teal-600" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <div className="text-sm font-semibold text-slate-700">Sincronizando con Sippy…</div>
                        <div className="text-xs text-slate-400">Esto puede tardar, no cierres la pestaña.</div>
                    </div>
                </div>
            )}
            <Alert kind="error" onClose={() => setError("")}>{error}</Alert>
            <Alert kind="ok" onClose={() => setOk("")}>{ok}</Alert>

            <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="min-w-64 flex-1">
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar: cuenta, IP, CLI/CLD, empresa, origen, estado, usuario…"
                    />
                </div>
                <span className="text-sm text-slate-400">{total} registros</span>
                <Btn variant="subtle" onClick={() => setOrden(orden === "desc" ? "asc" : "desc")}>
                    creado {orden === "desc" ? "↓ desc" : "↑ asc"}
                </Btn>
                <Btn variant="ghost" onClick={sincronizar} disabled={loading || sincronizando}>
                    {sincronizando ? "Sincronizando…" : "Sincronizar desde Sippy"}
                </Btn>
                <Btn variant="primary" onClick={reAlta} disabled={loading || sincronizando || sel.size === 0}>
                    Volver a dar de alta{sel.size > 0 ? ` (${sel.size})` : ""}
                </Btn>
            </div>

            {rows.length === 0 && !loading ? (
                <Empty>{q ? "Sin coincidencias para la busqueda." : "Sin historial."}</Empty>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full border-collapse whitespace-nowrap text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <Th>
                                    <input
                                        type="checkbox"
                                        checked={sel.size === rows.length && rows.length > 0}
                                        onChange={toggleTodos}
                                        className="accent-teal-600"
                                    />
                                </Th>
                                <Th>i_auth</Th><Th>cuenta</Th><Th>empresa / cliente</Th><Th>proto</Th>
                                <Th>remote_ip</Th><Th>incoming_cli</Th><Th>incoming_cld</Th>
                                <Th>cli_tr_rule</Th><Th>cld_tr_rule</Th><Th>to_domain</Th><Th>from_domain</Th>
                                <Th>estado</Th><Th>origen</Th><Th>creado_por</Th><Th>creado</Th><Th>borrado</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className={sel.has(r.id) ? "bg-teal-50/60" : "hover:bg-slate-50"}>
                                    <Td>
                                        <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} className="accent-teal-600" />
                                    </Td>
                                    <Td mono>{r.i_authentication ?? "—"}</Td>
                                    <Td mono>{r.i_account}</Td>
                                    <Td>{[r.empresa_nombre, r.cliente_nombre].filter(Boolean).join(" / ") || "—"}</Td>
                                    <Td mono>{r.i_protocol ?? "—"}</Td>
                                    <Td mono>{r.remote_ip || "—"}</Td>
                                    <Td mono>{r.incoming_cli || "—"}</Td>
                                    <Td mono>{r.incoming_cld || "—"}</Td>
                                    <Td mono>{r.cli_translation_rule || "—"}</Td>
                                    <Td mono>{r.cld_translation_rule || "—"}</Td>
                                    <Td mono>{r.to_domain || "—"}</Td>
                                    <Td mono>{r.from_domain || "—"}</Td>
                                    <Td>{r.estado === "activa" ? <Badge tone="teal">activa</Badge> : <Badge tone="rose">borrada</Badge>}</Td>
                                    <Td><Badge tone="slate">{r.origen}</Badge></Td>
                                    <Td>{r.creado_por || "—"}</Td>
                                    <Td>{fecha(r.creado_en)}</Td>
                                    <Td>{r.borrado_en ? `${fecha(r.borrado_en)}${r.borrado_por ? ` · ${r.borrado_por}` : ""}` : "—"}</Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-4 flex items-center justify-between">
                <span className="text-sm text-slate-400">Pagina {page} de {totalPages}</span>
                <div className="flex gap-2">
                    <Btn variant="subtle" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>Anterior</Btn>
                    <Btn variant="subtle" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>Siguiente</Btn>
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════

export default function Bloqueos() {
    const [tab, setTab] = useState("generar");

    return (
        <div className="mx-auto max-w-6xl px-4 py-6">
            <div className="mb-4">
                <h1 className="text-xl font-semibold text-slate-800">Bloqueos · Clearcom</h1>
                <p className="text-sm text-slate-500">Genera reglas de golpe, gestiona las reglas vivas y revisa el historial.</p>
            </div>

            <Tabs tabs={TABS} active={tab} onChange={setTab} />

            {tab === "generar" && <GenerarTab />}
            {tab === "reglas" && <ReglasTab onNueva={() => setTab("generar")} />}
            {tab === "historial" && <HistorialTab />}
        </div>
    );
}